import React, { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

// A consistent, cross-browser date field (identical in Safari & Chrome).
// Users can TYPE (DD/MM/YYYY, DD-MM-YYYY, or YYYY-MM-DD) OR pick from a
// calendar popover that has month + year dropdowns. Value in/out is the
// canonical ISO "YYYY-MM-DD" string (or "" when empty), so it's a drop-in
// replacement for <input type="date">.

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const pad = (n) => String(n).padStart(2, "0");

function parseISO(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  if (!m) return null;
  const y = +m[1];
  const mo = +m[2];
  const d = +m[3];
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d)
    return null;
  return { y, mo, d };
}

const isoToDisplay = (iso) => {
  const p = parseISO(iso);
  return p ? `${pad(p.d)}/${pad(p.mo)}/${p.y}` : "";
};

// Accepts DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY, or YYYY-MM-DD. Returns an ISO
// string, or null when the text can't be parsed into a real calendar date.
function displayToISO(str) {
  const s = (str || "").trim();
  if (!s) return "";
  let y;
  let mo;
  let d;
  let m;
  if ((m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s))) {
    [, y, mo, d] = m.map(Number);
  } else if ((m = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/.exec(s))) {
    d = +m[1];
    mo = +m[2];
    y = +m[3];
    if (y < 100) y += 2000;
  } else {
    return null;
  }
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d)
    return null;
  return `${y}-${pad(mo)}-${pad(d)}`;
}

const daysInMonth = (y, mo) => new Date(y, mo, 0).getDate(); // mo is 1-12

export default function DateField({
  value = "",
  onChange,
  min,
  max,
  placeholder = "DD/MM/YYYY",
  disabled = false,
  className = "",
  id,
  name,
  fromYear,
  toYear,
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(isoToDisplay(value));
  const inputRef = useRef(null);

  // Keep the visible text in sync when the value changes from outside.
  useEffect(() => {
    setText(isoToDisplay(value));
  }, [value]);

  const parsed = parseISO(value);
  const today = new Date();
  const nowYear = today.getFullYear();
  const startYear = fromYear ?? 1920;
  const endYear = toYear ?? nowYear + 60;

  // Which month the calendar is looking at.
  const [view, setView] = useState(() => ({
    y: parsed?.y ?? nowYear,
    mo: parsed?.mo ?? today.getMonth() + 1,
  }));

  useEffect(() => {
    if (open && parsed) setView({ y: parsed.y, mo: parsed.mo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const years = useMemo(() => {
    const arr = [];
    for (let y = endYear; y >= startYear; y--) arr.push(y);
    return arr;
  }, [startYear, endYear]);

  const commitText = () => {
    const iso = displayToISO(text);
    if (iso === null) {
      // Unparseable — revert to the last good value.
      setText(isoToDisplay(value));
      return;
    }
    if (iso !== value) onChange?.(iso);
  };

  const outOfRange = (iso) => (min && iso < min) || (max && iso > max);

  const pickDay = (d) => {
    const iso = `${view.y}-${pad(view.mo)}-${pad(d)}`;
    if (outOfRange(iso)) return;
    onChange?.(iso);
    setText(isoToDisplay(iso));
    setOpen(false);
  };

  const shiftMonth = (delta) => {
    setView((v) => {
      let mo = v.mo + delta;
      let y = v.y;
      if (mo < 1) {
        mo = 12;
        y -= 1;
      } else if (mo > 12) {
        mo = 1;
        y += 1;
      }
      return { y, mo };
    });
  };

  // Build the day grid (leading blanks for the first weekday).
  const firstWeekday = new Date(view.y, view.mo - 1, 1).getDay();
  const totalDays = daysInMonth(view.y, view.mo);
  const cells = [
    ...Array(firstWeekday).fill(null),
    ...Array.from({ length: totalDays }, (_, i) => i + 1),
  ];

  const selKey = parsed ? `${parsed.y}-${parsed.mo}` : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={`relative ${className}`}>
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          disabled={disabled}
          value={text}
          placeholder={placeholder}
          onChange={(e) => setText(e.target.value)}
          onBlur={commitText}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitText();
            }
          }}
          className="input-soft !pr-10"
          {...rest}
        />
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label="Open calendar"
            className="absolute right-2 top-1/2 -translate-y-1/2 grid h-7 w-7 place-items-center rounded-md text-slate-400 hover:text-emerald-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition disabled:opacity-50"
          >
            <CalendarDays className="h-4 w-4" />
          </button>
        </PopoverTrigger>
      </div>

      <PopoverContent
        align="start"
        className="w-[18rem] p-3 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
      >
        {/* Month / year controls */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => shiftMonth(-1)}
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <select
            value={view.mo}
            onChange={(e) => setView((v) => ({ ...v, mo: Number(e.target.value) }))}
            className="flex-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          >
            {MONTHS.map((mLabel, i) => (
              <option key={mLabel} value={i + 1}>
                {mLabel}
              </option>
            ))}
          </select>
          <select
            value={view.y}
            onChange={(e) => setView((v) => ({ ...v, y: Number(e.target.value) }))}
            className="w-[5.5rem] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => shiftMonth(1)}
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Weekday header */}
        <div className="mt-3 grid grid-cols-7 gap-1">
          {WEEKDAYS.map((w) => (
            <div
              key={w}
              className="text-center text-[11px] font-medium text-slate-400 py-1"
            >
              {w}
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (d === null) return <div key={`b-${i}`} />;
            const iso = `${view.y}-${pad(view.mo)}-${pad(d)}`;
            const isSelected =
              selKey === `${view.y}-${view.mo}` && parsed?.d === d;
            const isToday =
              today.getFullYear() === view.y &&
              today.getMonth() + 1 === view.mo &&
              today.getDate() === d;
            const disabledDay = outOfRange(iso);
            return (
              <button
                key={iso}
                type="button"
                disabled={disabledDay}
                onClick={() => pickDay(d)}
                className={`h-8 w-8 rounded-lg text-sm transition ${
                  isSelected
                    ? "bg-emerald-500 text-white font-semibold"
                    : isToday
                      ? "border border-emerald-400 text-emerald-600"
                      : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                } disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent`}
              >
                {d}
              </button>
            );
          })}
        </div>

        {/* Footer: quick clear + today */}
        <div className="mt-3 flex items-center justify-between border-t border-slate-100 dark:border-slate-800 pt-2">
          <button
            type="button"
            onClick={() => {
              onChange?.("");
              setText("");
              setOpen(false);
            }}
            className="text-xs font-medium text-slate-500 hover:text-rose-600"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => {
              const iso = `${nowYear}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
              if (outOfRange(iso)) {
                setView({ y: nowYear, mo: today.getMonth() + 1 });
                return;
              }
              pickDay(today.getDate());
              setView({ y: nowYear, mo: today.getMonth() + 1 });
            }}
            className="text-xs font-medium text-emerald-600 hover:text-emerald-700"
          >
            Today
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
